import { useCallback, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import PanelistDashboard from './PanelistDashboard';
import { AssessmentQualityInsights } from '@/components/dashboard/AssessmentQualityInsights';
import { AssessmentRoundsTable } from '@/components/dashboard/AssessmentRoundsTable';
import { ATSStatsCards } from '@/components/dashboard/ATSStatsCards';
import { CollegeSummaryTable } from '@/components/dashboard/CollegeSummaryTable';
import { HiringFunnel } from '@/components/dashboard/HiringFunnel';
import { MonthlyHiringTrend } from '@/components/dashboard/MonthlyHiringTrend';
import { PendingActionsCards } from '@/components/dashboard/PendingActionsCards';
import { RiskAlertsTable } from '@/components/dashboard/RiskAlertsTable';
import { RoleSummaryTable } from '@/components/dashboard/RoleSummaryTable';
import { RoleTargetTracker } from '@/components/dashboard/RoleTargetTracker';
import { SimplePieChart } from '@/components/dashboard/SimplePieChart';
import { StageStudentTable } from '@/components/dashboard/StageStudentTable';
import { UpcomingCollegesTable } from '@/components/dashboard/UpcomingCollegesTable';
import { UpcomingWorkloadCard } from '@/components/dashboard/UpcomingWorkloadCard';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuthorization } from '@/hooks/useAuthorization';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { CANDIDATE_STATUS, EVALUATION_ROUNDS, USER_ROLES } from '@/types/enums';

type DashboardCandidate = {
  id: string;
  full_name: string;
  status: string;
  created_at: string;
  updated_at: string;
  assesment_started_at: string | null;
  assessment_completed_at: string | null;
  joining_date: string | null;
  evaluation_email_sent: boolean;
  college_id: string | null;
  job_position_id: string | null;
  interviewer: string[] | null;
  current_round: string | null;
  colleges: {
    name: string;
    is_deleted: boolean | null;
    is_enabled: boolean;
  } | null;
  job_positions: {
    role_name: string;
  } | null;
};

type DashboardCollege = {
  id: string;
  name: string;
  college_code: string;
  college_visit_date: string | null;
  drive_start_date: string | null;
  is_enabled: boolean;
};

type DashboardRole = {
  id: string;
  role_name: string;
  target_hires: number;
};

type DashboardRound = {
  id: string;
  candidate_id: string;
  round_name: string;
  status: string | null;
  score: number | null;
  evaluated_at: string;
  candidates: {
    id: string;
    full_name: string;
    created_at: string;
    interviewer: string[] | null;
    colleges: {
      name: string;
      is_deleted: boolean | null;
      is_enabled: boolean;
    } | null;
    job_positions: {
      role_name: string;
    } | null;
  } | null;
};

type DashboardQuickLinkCard = {
  label: string;
  value: number;
  description: string;
  href: string;
};

function DashboardQuickLinks({ cards }: { cards: DashboardQuickLinkCard[] }) {
  return (
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
      {cards.map((card) => (
        <Link key={card.label} to={card.href} className="block">
          <Card className="h-full transition-colors hover:bg-muted/30">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">{card.label}</CardTitle>
              <CardDescription>{card.description}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{card.value}</div>
            </CardContent>
          </Card>
        </Link>
      ))}
    </div>
  );
}

const ASSESSMENT_SENT_OR_LATER_STATUSES = new Set<string>([
  CANDIDATE_STATUS.ASSESSMENT_SENT,
  CANDIDATE_STATUS.ASSESSMENT_IN_PROGRESS,
  CANDIDATE_STATUS.ASSESSMENT_COMPLETED,
  CANDIDATE_STATUS.SELECTED,
  CANDIDATE_STATUS.OFFERED,
  CANDIDATE_STATUS.ACCEPTED,
  CANDIDATE_STATUS.JOINED,
  CANDIDATE_STATUS.FAILED,
  CANDIDATE_STATUS.HOLD,
  CANDIDATE_STATUS.DECLINED,
  CANDIDATE_STATUS.PASSED,
]);

const ASSESSMENT_STARTED_OR_LATER_STATUSES = new Set<string>([
  CANDIDATE_STATUS.ASSESSMENT_IN_PROGRESS,
  CANDIDATE_STATUS.ASSESSMENT_COMPLETED,
  CANDIDATE_STATUS.SELECTED,
  CANDIDATE_STATUS.OFFERED,
  CANDIDATE_STATUS.ACCEPTED,
  CANDIDATE_STATUS.JOINED,
  CANDIDATE_STATUS.FAILED,
  CANDIDATE_STATUS.HOLD,
  CANDIDATE_STATUS.DECLINED,
  CANDIDATE_STATUS.PASSED,
]);

const ASSESSMENT_COMPLETED_OR_LATER_STATUSES = new Set<string>([
  CANDIDATE_STATUS.ASSESSMENT_COMPLETED,
  CANDIDATE_STATUS.SELECTED,
  CANDIDATE_STATUS.OFFERED,
  CANDIDATE_STATUS.ACCEPTED,
  CANDIDATE_STATUS.JOINED,
  CANDIDATE_STATUS.FAILED,
  CANDIDATE_STATUS.HOLD,
  CANDIDATE_STATUS.DECLINED,
  CANDIDATE_STATUS.PASSED,
]);

const SELECTED_OR_LATER_STATUSES = new Set<string>([
  CANDIDATE_STATUS.SELECTED,
  CANDIDATE_STATUS.OFFERED,
  CANDIDATE_STATUS.ACCEPTED,
  CANDIDATE_STATUS.JOINED,
]);

const OFFERED_OR_LATER_STATUSES = new Set<string>([
  CANDIDATE_STATUS.OFFERED,
  CANDIDATE_STATUS.ACCEPTED,
  CANDIDATE_STATUS.JOINED,
]);

const ACCEPTED_OR_LATER_STATUSES = new Set<string>([
  CANDIDATE_STATUS.ACCEPTED,
  CANDIDATE_STATUS.JOINED,
]);

const FINAL_STAGE_ROUNDS = [
  EVALUATION_ROUNDS.OFFICE_TECHNICAL,
  EVALUATION_ROUNDS.HR_FOUNDER,
];

const FINAL_STAGE_STATUSES = [
  CANDIDATE_STATUS.PASSED,
  CANDIDATE_STATUS.SELECTED,
  CANDIDATE_STATUS.ACCEPTED,
  CANDIDATE_STATUS.JOINED,
];

const PASSED_ROUND_STATUSES = new Set<string>([
  CANDIDATE_STATUS.PASSED,
  CANDIDATE_STATUS.SELECTED,
  CANDIDATE_STATUS.ACCEPTED,
  CANDIDATE_STATUS.JOINED,
]);

const toPercent = (value: number, total: number) => {
  if (total <= 0) return 0;
  return Math.round((value / total) * 1000) / 10;
};

const toRatio = (value: number, total: number) => {
  if (total <= 0) return null;
  return Math.round((value / total) * 10) / 10;
};

const getMedian = (scores: number[]) => {
  if (scores.length === 0) return 0;
  const sorted = [...scores].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);

  if (sorted.length % 2 === 0) {
    return (sorted[middle - 1] + sorted[middle]) / 2;
  }

  return sorted[middle];
};

const DASHBOARD_BATCH_SIZE = 1000;
// Supabase free-tier connection limit is low; keep parallel fetches bounded
const MAX_PARALLEL_BATCHES = 3;

async function fetchAllBatches<T>(
  fetchBatch: (from: number, to: number) => Promise<{ data: T[] | null; error: unknown }>
): Promise<T[]> {
  // First batch to discover the total size
  const { data: first, error: firstErr } = await fetchBatch(0, DASHBOARD_BATCH_SIZE - 1);
  if (firstErr) throw firstErr;

  const firstBatch = first || [];
  if (firstBatch.length < DASHBOARD_BATCH_SIZE) return firstBatch;

  // Fire remaining batches in parallel (bounded concurrency)
  const rows: T[] = [...firstBatch];
  let from = DASHBOARD_BATCH_SIZE;
  let done = false;

  while (!done) {
    const promises: Promise<{ data: T[] | null; error: unknown }>[] = [];

    for (let i = 0; i < MAX_PARALLEL_BATCHES; i++) {
      const batchFrom = from + i * DASHBOARD_BATCH_SIZE;
      const batchTo = batchFrom + DASHBOARD_BATCH_SIZE - 1;
      promises.push(fetchBatch(batchFrom, batchTo));
    }

    const results = await Promise.all(promises);

    for (const result of results) {
      if (result.error) throw result.error;
      const batch = result.data || [];
      rows.push(...batch);
      if (batch.length < DASHBOARD_BATCH_SIZE) {
        done = true;
        break;
      }
    }

    from += MAX_PARALLEL_BATCHES * DASHBOARD_BATCH_SIZE;
  }

  return rows;
}

const hasAssessmentSent = (candidate: DashboardCandidate) => (
  candidate.evaluation_email_sent ||
  ASSESSMENT_SENT_OR_LATER_STATUSES.has(candidate.status)
);

const hasAssessmentStarted = (candidate: DashboardCandidate) => (
  Boolean(candidate.assesment_started_at) ||
  hasAssessmentCompleted(candidate) ||
  ASSESSMENT_STARTED_OR_LATER_STATUSES.has(candidate.status)
);

function hasAssessmentCompleted(candidate: DashboardCandidate) {
  return (
    Boolean(candidate.assessment_completed_at) ||
    ASSESSMENT_COMPLETED_OR_LATER_STATUSES.has(candidate.status)
  );
}

const hasReachedSelected = (candidate: DashboardCandidate) => (
  SELECTED_OR_LATER_STATUSES.has(candidate.status)
);

const hasReachedOffered = (candidate: DashboardCandidate) => (
  OFFERED_OR_LATER_STATUSES.has(candidate.status)
);

const hasReachedAccepted = (candidate: DashboardCandidate) => (
  ACCEPTED_OR_LATER_STATUSES.has(candidate.status)
);

const hasReachedJoined = (candidate: DashboardCandidate) => (
  candidate.status === CANDIDATE_STATUS.JOINED
);

export default function Dashboard() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentYear = new Date().getFullYear();
  const currentYearStr = currentYear.toString();
  const yearFilter = searchParams.get('year') ?? currentYearStr;
  const { role, loading } = useAuthorization();
  const isHr = role === USER_ROLES.HR;

  const setYearFilter = (value: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);

      if (!value || value === currentYearStr) {
        next.delete('year');
      } else {
        next.set('year', value);
      }

      return next;
    });
  };

  const queriesEnabled = !!user && !loading && role !== USER_ROLES.PANELIST;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayIso = today.toISOString();

  const yearOptions = useMemo(
    () => Array.from({ length: 5 }, (_, index) => (currentYear - index).toString()),
    [currentYear]
  );

  const yearBounds = useMemo(() => {
    if (yearFilter === 'all') return null;

    const year = Number.parseInt(yearFilter, 10);
    if (Number.isNaN(year)) return null;

    return {
      start: new Date(Date.UTC(year, 0, 1)).toISOString(),
      end: new Date(Date.UTC(year + 1, 0, 1)).toISOString(),
    };
  }, [yearFilter]);

  const isWithinYear = useCallback((date: string | null | undefined) => {
    if (!yearBounds) return true;
    if (!date) return false;
    return date >= yearBounds.start && date < yearBounds.end;
  }, [yearBounds]);

  const getCandidatesHref = useCallback((filters?: Record<string, string>) => {
    const params = new URLSearchParams();

    if (yearFilter === 'all') {
      params.set('year', 'all');
    } else if (yearFilter !== currentYearStr) {
      params.set('year', yearFilter);
    }

    Object.entries(filters ?? {}).forEach(([key, value]) => {
      if (value) {
        params.set(key, value);
      }
    });

    const query = params.toString();
    return query ? `/admin/candidates?${query}` : '/admin/candidates';
  }, [currentYearStr, yearFilter]);

  const refreshMutation = useMutation({
    mutationFn: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['dashboard-colleges'] }),
        queryClient.invalidateQueries({ queryKey: ['dashboard-candidates'] }),
        queryClient.invalidateQueries({ queryKey: ['dashboard-roles'] }),
        queryClient.invalidateQueries({ queryKey: ['dashboard-rounds'] }),
      ]);
    },
    onSuccess: () => {
      toast({ title: 'Success', description: 'Dashboard data refreshed.' });
    },
    onError: (err: any) => {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    },
  });

  const { data: colleges = [], isLoading: collegesLoading } = useQuery({
    queryKey: ['dashboard-colleges'],
    enabled: queriesEnabled,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('colleges')
        .select('id, name, college_code, college_visit_date, drive_start_date, is_enabled')
        .eq('is_deleted', false)
        .order('name', { ascending: true });

      if (error) throw error;
      return (data || []) as unknown as DashboardCollege[];
    },
  });

  const { data: roles = [], isLoading: rolesLoading } = useQuery({
    queryKey: ['dashboard-roles'],
    enabled: queriesEnabled,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('job_positions')
        .select('id, role_name, target_hires')
        .eq('is_active', true)
        .order('role_name', { ascending: true });

      if (error) throw error;
      return (data || []) as unknown as DashboardRole[];
    },
  });

  const { data: candidates = [], isLoading: candidatesLoading } = useQuery({
    queryKey: ['dashboard-candidates'],
    enabled: queriesEnabled,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const data = await fetchAllBatches<DashboardCandidate>(async (from, to) => {
        const response = await supabase
          .from('candidates')
          .select(`
            id,
            full_name,
            status,
            created_at,
            updated_at,
            assesment_started_at,
            assessment_completed_at,
            joining_date,
            evaluation_email_sent,
            college_id,
            job_position_id,
            interviewer,
            current_round,
            colleges(name, is_deleted, is_enabled),
            job_positions(role_name)
          `)
          .range(from, to);

        return {
          data: (response.data || []) as unknown as DashboardCandidate[],
          error: response.error,
        };
      });

      return data;
    },
  });

  const { data: rounds = [], isLoading: roundsLoading } = useQuery({
    queryKey: ['dashboard-rounds'],
    enabled: queriesEnabled,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const data = await fetchAllBatches<DashboardRound>(async (from, to) => {
        const response = await supabase
          .from('evaluation_rounds')
          .select(`
            id,
            candidate_id,
            round_name,
            status,
            score,
            evaluated_at,
            candidates(
              id,
              full_name,
              created_at,
              interviewer,
              colleges(name, is_deleted, is_enabled),
              job_positions(role_name)
            )
          `)
          .range(from, to);

        return {
          data: (response.data || []) as unknown as DashboardRound[],
          error: response.error,
        };
      });

      return data;
    },
  });

  const filteredCandidates = useMemo(
    () => candidates.filter((candidate) => (
      candidate.colleges?.is_deleted !== true &&
      candidate.colleges?.is_enabled !== false &&
      isWithinYear(candidate.created_at)
    )),
    [candidates, isWithinYear]
  );

  const filteredRounds = useMemo(
    () => rounds.filter((round) => {
      const candidate = round.candidates;
      return (
        candidate?.colleges?.is_deleted !== true &&
        candidate?.colleges?.is_enabled !== false &&
        isWithinYear(candidate?.created_at)
      );
    }),
    [isWithinYear, rounds]
  );

  const enabledColleges = useMemo(
    () => colleges.filter((college) => college.is_enabled !== false),
    [colleges]
  );

  const collegesInScope = useMemo(
    () => enabledColleges.filter((college) => (
      !yearBounds || isWithinYear(college.college_visit_date)
    )),
    [enabledColleges, isWithinYear, yearBounds]
  );

  const atsStats = useMemo(() => {
    const upcoming = collegesInScope.filter((college) => (
      Boolean(college.college_visit_date) && college.college_visit_date! >= todayIso
    )).length;
    const completed = collegesInScope.filter((college) => (
      Boolean(college.college_visit_date) && college.college_visit_date! < todayIso
    )).length;

    return {
      totalColleges: collegesInScope.length,
      upcoming,
      completed,
      totalStudents: filteredCandidates.length,
      totalSelected: filteredCandidates.filter((candidate) => candidate.status === CANDIDATE_STATUS.SELECTED).length,
      totalOffered: filteredCandidates.filter((candidate) => candidate.status === CANDIDATE_STATUS.OFFERED).length,
      totalAccepted: filteredCandidates.filter((candidate) => candidate.status === CANDIDATE_STATUS.ACCEPTED).length,
      totalJoined: filteredCandidates.filter((candidate) => candidate.status === CANDIDATE_STATUS.JOINED).length,
    };
  }, [collegesInScope, filteredCandidates, todayIso]);

  const funnelData = useMemo(() => {
    const submitted = filteredCandidates.length;
    const assessmentSent = filteredCandidates.filter(hasAssessmentSent).length;
    const assessmentStarted = filteredCandidates.filter(hasAssessmentStarted).length;
    const assessmentCompleted = filteredCandidates.filter(hasAssessmentCompleted).length;
    const selected = filteredCandidates.filter(hasReachedSelected).length;
    const offered = filteredCandidates.filter(hasReachedOffered).length;
    const accepted = filteredCandidates.filter(hasReachedAccepted).length;
    const joined = filteredCandidates.filter(hasReachedJoined).length;

    const stages = [
      { label: 'CV Submitted', count: submitted, previousCount: submitted },
      { label: 'Assessment Sent', count: assessmentSent, previousCount: submitted },
      { label: 'Assessment Started', count: assessmentStarted, previousCount: assessmentSent },
      { label: 'Assessment Completed', count: assessmentCompleted, previousCount: assessmentStarted },
      { label: 'Selected', count: selected, previousCount: assessmentCompleted },
      { label: 'Offered', count: offered, previousCount: selected },
      { label: 'Accepted', count: accepted, previousCount: offered },
      { label: 'Joined', count: joined, previousCount: accepted },
    ];

    return stages.map((stage, index) => ({
      label: stage.label,
      count: stage.count,
      conversionRate: index === 0 ? 100 : toPercent(stage.count, stage.previousCount),
    }));
  }, [filteredCandidates]);

  const pendingActions = useMemo(() => ([
    {
      label: 'Assessment Completed, Awaiting Shortlist',
      count: filteredCandidates.filter((candidate) => (
        hasAssessmentCompleted(candidate) &&
        !hasReachedSelected(candidate) &&
        candidate.status !== CANDIDATE_STATUS.FAILED
      )).length,
      description: 'Candidates finished assessment but are not yet shortlisted.',
    },
    {
      label: 'Selected, Offer Pending',
      count: filteredCandidates.filter((candidate) => candidate.status === CANDIDATE_STATUS.SELECTED).length,
      description: 'Candidates shortlisted but not yet moved to offer stage.',
    },
    {
      label: 'Offered, Awaiting Response',
      count: filteredCandidates.filter((candidate) => candidate.status === CANDIDATE_STATUS.OFFERED).length,
      description: 'Candidates who received offers and still need a response.',
    },
    {
      label: 'Accepted, Joining Date Missing',
      count: filteredCandidates.filter((candidate) => (
        candidate.status === CANDIDATE_STATUS.ACCEPTED && !candidate.joining_date
      )).length,
      description: 'Accepted candidates without a confirmed joining date.',
    },
  ]), [filteredCandidates]);

  const upcomingColleges = useMemo(() => {
    const candidateCounts = new Map<string, number>();

    filteredCandidates.forEach((candidate) => {
      if (!candidate.college_id) return;
      candidateCounts.set(candidate.college_id, (candidateCounts.get(candidate.college_id) || 0) + 1);
    });

    return collegesInScope
      .filter((college) => Boolean(college.college_visit_date) && college.college_visit_date! >= todayIso)
      .sort((a, b) => (a.college_visit_date || '').localeCompare(b.college_visit_date || ''))
      .slice(0, 10)
      .map((college) => ({
        id: college.id,
        name: college.name,
        college_visit_date: college.college_visit_date,
        drive_start_date: college.drive_start_date,
        cv_submitted_count: candidateCounts.get(college.id) || 0,
      }));
  }, [collegesInScope, filteredCandidates, todayIso]);

  const upcomingWorkload = useMemo(() => {
    type WorkloadUrgency = 'next_7_days' | 'next_14_days' | 'later';
    const now = new Date(todayIso).getTime();
    const sevenDaysFromNow = now + 7 * 24 * 60 * 60 * 1000;
    const fourteenDaysFromNow = now + 14 * 24 * 60 * 60 * 1000;
    const candidateCounts = new Map<string, number>();

    filteredCandidates.forEach((candidate) => {
      if (!candidate.college_id) return;
      candidateCounts.set(candidate.college_id, (candidateCounts.get(candidate.college_id) || 0) + 1);
    });

    const rows = collegesInScope
      .filter((college) => {
        if (!college.college_visit_date) return false;
        const visitTime = new Date(college.college_visit_date).getTime();
        return visitTime >= now;
      })
      .map((college) => {
        const visitTime = new Date(college.college_visit_date as string).getTime();
        const urgency: WorkloadUrgency = visitTime <= sevenDaysFromNow
          ? 'next_7_days'
          : visitTime <= fourteenDaysFromNow
            ? 'next_14_days'
            : 'later';

        return {
          id: college.id,
          college: college.name,
          visitDate: college.college_visit_date,
          candidateCount: candidateCounts.get(college.id) || 0,
          windowLabel: urgency === 'next_7_days' ? 'Next 7 days' : urgency === 'next_14_days' ? 'Next 14 days' : 'Later',
          urgency,
        };
      })
      .sort((a, b) => {
        if (a.urgency !== b.urgency) {
          const order = { next_7_days: 0, next_14_days: 1, later: 2 };
          return order[a.urgency] - order[b.urgency];
        }
        return b.candidateCount - a.candidateCount;
      })
      .slice(0, 8);

    return {
      next7Days: rows.filter((row) => row.urgency === 'next_7_days').length,
      next14Days: rows.filter((row) => row.urgency === 'next_7_days' || row.urgency === 'next_14_days').length,
      rows,
    };
  }, [collegesInScope, filteredCandidates, todayIso]);

  const assessmentRounds = useMemo(() => {
    const grouped = new Map<string, { total_taken: number; scoreSum: number; scoreCount: number }>();

    filteredRounds.forEach((round) => {
      if (!round.round_name) return;

      const current = grouped.get(round.round_name) || {
        total_taken: 0,
        scoreSum: 0,
        scoreCount: 0,
      };

      current.total_taken += 1;
      if (typeof round.score === 'number') {
        current.scoreSum += round.score;
        current.scoreCount += 1;
      }

      grouped.set(round.round_name, current);
    });

    return Array.from(grouped.entries()).map(([round_name, value]) => ({
      round_name,
      total_taken: value.total_taken,
      average_score: value.scoreCount > 0 ? value.scoreSum / value.scoreCount : 0,
    }));
  }, [filteredRounds]);

  const collegeSummary = useMemo(() => {
    const rows = new Map<string, {
      college: string;
      total: number;
      completed: number;
      selected: number;
      offered: number;
      accepted: number;
      joined: number;
      selectionRate: number;
      joiningRate: number;
    }>();

    enabledColleges.forEach((college) => {
      rows.set(college.id, {
        college: college.name,
        total: 0,
        completed: 0,
        selected: 0,
        offered: 0,
        accepted: 0,
        joined: 0,
        selectionRate: 0,
        joiningRate: 0,
      });
    });

    filteredCandidates.forEach((candidate) => {
      if (!candidate.college_id || !rows.has(candidate.college_id)) return;

      const row = rows.get(candidate.college_id)!;
      row.total += 1;
      if (hasAssessmentCompleted(candidate)) row.completed += 1;
      if (hasReachedSelected(candidate)) row.selected += 1;
      if (hasReachedOffered(candidate)) row.offered += 1;
      if (hasReachedAccepted(candidate)) row.accepted += 1;
      if (hasReachedJoined(candidate)) row.joined += 1;
    });

    return Array.from(rows.values())
      .map((row) => ({
        ...row,
        selectionRate: toPercent(row.selected, row.total),
        joiningRate: toPercent(row.joined, row.total),
      }))
      .filter((row) => row.total > 0)
      .sort((a, b) => b.total - a.total);
  }, [enabledColleges, filteredCandidates]);

  const roleSummary = useMemo(() => {
    const rows = new Map<string, {
      role: string;
      total: number;
      completed: number;
      selected: number;
      offered: number;
      accepted: number;
      joined: number;
      selectionRate: number;
      joiningRate: number;
    }>();

    roles.forEach((item) => {
      rows.set(item.id, {
        role: item.role_name,
        total: 0,
        completed: 0,
        selected: 0,
        offered: 0,
        accepted: 0,
        joined: 0,
        selectionRate: 0,
        joiningRate: 0,
      });
    });

    filteredCandidates.forEach((candidate) => {
      if (!candidate.job_position_id || !rows.has(candidate.job_position_id)) return;

      const row = rows.get(candidate.job_position_id)!;
      row.total += 1;
      if (hasAssessmentCompleted(candidate)) row.completed += 1;
      if (hasReachedSelected(candidate)) row.selected += 1;
      if (hasReachedOffered(candidate)) row.offered += 1;
      if (hasReachedAccepted(candidate)) row.accepted += 1;
      if (hasReachedJoined(candidate)) row.joined += 1;
    });

    return Array.from(rows.values())
      .map((row) => ({
        ...row,
        selectionRate: toPercent(row.selected, row.total),
        joiningRate: toPercent(row.joined, row.total),
      }))
      .filter((row) => row.total > 0)
      .sort((a, b) => b.total - a.total);
  }, [filteredCandidates, roles]);

  const roleTargetRows = useMemo(() => {
    return roles
      .map((role) => {
        const summary = roleSummary.find((item) => item.role === role.role_name);
        const target = role.target_hires ?? 0;
        const selected = summary?.selected ?? 0;
        const accepted = summary?.accepted ?? 0;
        const joined = summary?.joined ?? 0;
        const selectedRatio = toRatio(selected, target);
        const acceptedRatio = toRatio(accepted, target);
        const joinedRatio = toRatio(joined, target);

        let status: 'achieved' | 'healthy' | 'watch' | 'at_risk' | 'no_target' = 'no_target';

        if (target > 0) {
          if (joined >= target) {
            status = 'achieved';
          } else if (selected >= target * 3 && accepted >= target * 2) {
            status = 'healthy';
          } else if (selected >= target * 2 || accepted >= target * 1.5) {
            status = 'watch';
          } else {
            status = 'at_risk';
          }
        }

        return {
          roleId: role.id,
          role: role.role_name,
          target,
          selected,
          accepted,
          joined,
          selectedRatio,
          acceptedRatio,
          joinedRatio,
          gapToTarget: joined - target,
          status,
        };
      })
      .sort((a, b) => {
        if (a.status !== b.status) {
          const order = { at_risk: 0, watch: 1, healthy: 2, achieved: 3, no_target: 4 };
          return order[a.status] - order[b.status];
        }
        return b.joined - a.joined;
      });
  }, [roleSummary, roles]);

  const stageStudents = useMemo(() => {
    const latestRoundsByCandidate = new Map<string, DashboardRound>();

    filteredRounds.forEach((round) => {
      if (
        !FINAL_STAGE_ROUNDS.includes(round.round_name as typeof FINAL_STAGE_ROUNDS[number]) ||
        !FINAL_STAGE_STATUSES.includes(round.status as typeof FINAL_STAGE_STATUSES[number])
      ) {
        return;
      }

      const existing = latestRoundsByCandidate.get(round.candidate_id);
      if (!existing || round.evaluated_at > existing.evaluated_at) {
        latestRoundsByCandidate.set(round.candidate_id, round);
      }
    });

    return Array.from(latestRoundsByCandidate.values()).sort(
      (a, b) => b.evaluated_at.localeCompare(a.evaluated_at)
    );
  }, [filteredRounds]);

  const pieData = useMemo(() => {
    const colorPalette = [
      '#8884d8',
      '#82ca9d',
      '#ffc658',
      '#8dd1e1',
      '#a4de6c',
      '#ff7f7f',
      '#ffa500',
      '#8a2be2',
      '#ff69b4',
      '#00c9a7',
    ];

    const finalStageCandidateIds = new Set(stageStudents.map((row) => row.candidate_id));
    const collegeTotals = new Map<string, number>();
    const collegeStages = new Map<string, number>();
    const roleTotals = new Map<string, number>();
    const roleStages = new Map<string, number>();

    filteredCandidates.forEach((candidate) => {
      const collegeName = candidate.colleges?.name;
      const roleName = candidate.job_positions?.role_name;

      if (collegeName) {
        collegeTotals.set(collegeName, (collegeTotals.get(collegeName) || 0) + 1);
        if (finalStageCandidateIds.has(candidate.id)) {
          collegeStages.set(collegeName, (collegeStages.get(collegeName) || 0) + 1);
        }
      }

      if (roleName) {
        roleTotals.set(roleName, (roleTotals.get(roleName) || 0) + 1);
        if (finalStageCandidateIds.has(candidate.id)) {
          roleStages.set(roleName, (roleStages.get(roleName) || 0) + 1);
        }
      }
    });

    const toChart = (rows: Map<string, number>) => {
      const sorted = Array.from(rows.entries())
        .map(([name, value]) => ({ name, value }))
        .filter((item) => item.name)
        .sort((a, b) => b.value - a.value);

      const top = sorted.slice(0, 10);
      if (sorted.length > 10) {
        const othersTotal = sorted.slice(10).reduce((sum, item) => sum + item.value, 0);
        top.push({ name: 'Others', value: othersTotal });
      }

      return top.map((item, index) => ({
        ...item,
        color: colorPalette[index % colorPalette.length],
      }));
    };

    return {
      collegeTotals: toChart(collegeTotals),
      collegeStages: toChart(collegeStages),
      roleTotals: toChart(roleTotals),
      roleStages: toChart(roleStages),
    };
  }, [filteredCandidates, stageStudents]);

  const riskAlerts = useMemo(() => {
    const alerts: Array<{
      id: string;
      category: string;
      subject: string;
      detail: string;
      severity: 'high' | 'medium';
    }> = [];

    const now = new Date(todayIso).getTime();
    const fourteenDays = 14 * 24 * 60 * 60 * 1000;

    filteredCandidates.forEach((candidate) => {
      const updatedAt = new Date(candidate.updated_at).getTime();
      const daysSinceUpdate = Math.floor((now - updatedAt) / (24 * 60 * 60 * 1000));

      if (
        ![CANDIDATE_STATUS.JOINED, CANDIDATE_STATUS.FAILED, CANDIDATE_STATUS.DECLINED].includes(candidate.status as any) &&
        daysSinceUpdate >= 14
      ) {
        alerts.push({
          id: `stuck-${candidate.id}`,
          category: 'Stuck Candidate',
          subject: candidate.full_name,
          detail: `${candidate.colleges?.name || 'Unknown college'} - ${candidate.status} for ${daysSinceUpdate} days`,
          severity: daysSinceUpdate >= 21 ? 'high' : 'medium',
        });
      }

      if (candidate.status === CANDIDATE_STATUS.ACCEPTED && !candidate.joining_date) {
        alerts.push({
          id: `joining-${candidate.id}`,
          category: 'Joining Pending',
          subject: candidate.full_name,
          detail: `${candidate.colleges?.name || 'Unknown college'} - accepted candidate missing joining date`,
          severity: 'high',
        });
      }
    });

    collegeSummary.forEach((college) => {
      if (college.total >= 5 && college.completed / college.total < 0.5) {
        alerts.push({
          id: `college-${college.college}`,
          category: 'Low Completion',
          subject: college.college,
          detail: `${college.completed}/${college.total} candidates completed assessments`,
          severity: college.completed / college.total < 0.35 ? 'high' : 'medium',
        });
      }
    });

    roleSummary.forEach((role) => {
      if (role.total >= 5 && role.selectionRate < 20) {
        alerts.push({
          id: `role-${role.role}`,
          category: 'Low Selection Rate',
          subject: role.role,
          detail: `${role.selected}/${role.total} candidates selected (${role.selectionRate.toFixed(1)}%)`,
          severity: role.selectionRate < 10 ? 'high' : 'medium',
        });
      }
    });

    return alerts
      .sort((a, b) => {
        if (a.severity !== b.severity) {
          return a.severity === 'high' ? -1 : 1;
        }
        return a.category.localeCompare(b.category);
      })
      .slice(0, 10);
  }, [collegeSummary, filteredCandidates, roleSummary, todayIso]);

  const monthlyTrendData = useMemo(() => {
    const formatter = new Intl.DateTimeFormat('en-US', { month: 'short', timeZone: 'UTC' });
    const monthCount = yearBounds ? 12 : 6;
    const now = new Date();
    const months = Array.from({ length: monthCount }, (_, index) => {
      if (yearBounds) {
        const date = new Date(Date.UTC(Number.parseInt(yearFilter, 10), index, 1));
        return {
          key: date.toISOString().slice(0, 7),
          month: formatter.format(date),
          registrations: 0,
          completed: 0,
          selected: 0,
          joined: 0,
        };
      }

      const date = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - (monthCount - index - 1), 1));
      return {
        key: date.toISOString().slice(0, 7),
        month: formatter.format(date),
        registrations: 0,
        completed: 0,
        selected: 0,
        joined: 0,
      };
    });

    const rows = new Map(months.map((item) => [item.key, item]));

    const incrementMonth = (date: string | null | undefined, field: 'registrations' | 'completed' | 'selected' | 'joined') => {
      if (!date) return;
      const key = date.slice(0, 7);
      const row = rows.get(key);
      if (!row) return;
      row[field] += 1;
    };

    filteredCandidates.forEach((candidate) => {
      incrementMonth(candidate.created_at, 'registrations');
      if (hasAssessmentCompleted(candidate)) {
        incrementMonth(candidate.assessment_completed_at || candidate.updated_at, 'completed');
      }
      if (hasReachedSelected(candidate)) {
        incrementMonth(candidate.updated_at, 'selected');
      }
      if (hasReachedJoined(candidate)) {
        incrementMonth(candidate.joining_date || candidate.updated_at, 'joined');
      }
    });

    return Array.from(rows.values()).map(({ key: _key, ...item }) => item);
  }, [filteredCandidates, yearBounds, yearFilter]);

  const assessmentQualityData = useMemo(() => {
    const grouped = new Map<string, { attempts: number; scoreSum: number; scores: number[]; passed: number }>();

    filteredRounds.forEach((round) => {
      if (!round.round_name) return;

      const current = grouped.get(round.round_name) || {
        attempts: 0,
        scoreSum: 0,
        scores: [] as number[],
        passed: 0,
      };

      current.attempts += 1;
      if (typeof round.score === 'number') {
        current.scoreSum += round.score;
        current.scores.push(round.score);
      }
      if (round.status && PASSED_ROUND_STATUSES.has(round.status)) {
        current.passed += 1;
      }

      grouped.set(round.round_name, current);
    });

    return Array.from(grouped.entries())
      .map(([roundName, value]) => ({
        roundName,
        attempts: value.attempts,
        averageScore: value.scores.length > 0 ? value.scoreSum / value.scores.length : 0,
        medianScore: getMedian(value.scores),
        passRate: toPercent(value.passed, value.attempts),
      }))
      .sort((a, b) => b.attempts - a.attempts);
  }, [filteredRounds]);

  const hrQuickLinks = useMemo(() => {
    const selected = filteredCandidates.filter((candidate) => candidate.status === CANDIDATE_STATUS.SELECTED).length;
    const offered = filteredCandidates.filter((candidate) => candidate.status === CANDIDATE_STATUS.OFFERED).length;
    const accepted = filteredCandidates.filter((candidate) => candidate.status === CANDIDATE_STATUS.ACCEPTED).length;
    const joined = filteredCandidates.filter((candidate) => candidate.status === CANDIDATE_STATUS.JOINED).length;
    const joiningDateMissing = filteredCandidates.filter((candidate) => (
      candidate.status === CANDIDATE_STATUS.ACCEPTED && !candidate.joining_date
    )).length;

    return [
      {
        label: 'Total Candidates',
        value: filteredCandidates.length,
        description: 'All candidates in the current hiring pipeline.',
        href: getCandidatesHref(),
      },
      {
        label: 'Assessment Completed',
        value: filteredCandidates.filter(hasAssessmentCompleted).length,
        description: 'Candidates ready for shortlisting and interview decisions.',
        href: getCandidatesHref(),
      },
      {
        label: 'Final Stage Candidates',
        value: stageStudents.length,
        description: 'Candidates who have reached the final interview stages.',
        href: getCandidatesHref(),
      },
      {
        label: 'Offer Pending',
        value: selected,
        description: 'Selected candidates waiting for an offer to be sent.',
        href: getCandidatesHref({ status: CANDIDATE_STATUS.SELECTED }),
      },
      {
        label: 'Awaiting Response',
        value: offered,
        description: 'Candidates with offers who still need follow-up.',
        href: getCandidatesHref({ status: CANDIDATE_STATUS.OFFERED }),
      },
      {
        label: 'Accepted Offers',
        value: accepted,
        description: 'Candidates who accepted their offer and need joining coordination.',
        href: getCandidatesHref({ status: CANDIDATE_STATUS.ACCEPTED }),
      },
      {
        label: 'Joining Date Missing',
        value: joiningDateMissing,
        description: 'Accepted candidates without a confirmed joining date.',
        href: getCandidatesHref({ status: CANDIDATE_STATUS.ACCEPTED }),
      },
      {
        label: 'Joined',
        value: joined,
        description: 'Candidates who have completed the process and joined.',
        href: getCandidatesHref({ status: CANDIDATE_STATUS.JOINED }),
      },
    ] satisfies DashboardQuickLinkCard[];
  }, [filteredCandidates, getCandidatesHref, stageStudents]);

  const visibleRiskAlerts = useMemo(() => {
    if (!isHr) {
      return riskAlerts;
    }

    return riskAlerts.filter((alert) => alert.category !== 'Low Completion');
  }, [isHr, riskAlerts]);

  const dashboardLoading = collegesLoading || rolesLoading || candidatesLoading || roundsLoading;

  if (loading) {
    return <LoadingSpinner size="lg" text="Loading dashboard..." />;
  }

  if (!role) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>No Role Assigned</CardTitle>
          <CardDescription>
            Your account does not have a dashboard role yet. Ask an admin to assign `admin`, `hr`, or `panelist` in the `profiles` table.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  if (role === USER_ROLES.PANELIST) {
    return <PanelistDashboard />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {isHr ? 'HR Dashboard' : 'Dashboard'}
          </h1>
          <p className="text-muted-foreground">
            {isHr
              ? 'Monitor candidate progress, offers, and joining readiness.'
              : 'Monitor campus hiring activity across colleges, roles, and stages.'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            className="h-9 rounded-md border border-input bg-background px-3 text-sm"
            value={yearFilter}
            onChange={(event) => setYearFilter(event.target.value)}
          >
            <option value="all">All Years</option>
            {yearOptions.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
          <Button
            size="sm"
            variant="outline"
            disabled={refreshMutation.isPending}
            onClick={() => refreshMutation.mutate()}
          >
            Refresh Data
          </Button>
        </div>
      </div>

      {dashboardLoading ? (
        <LoadingSpinner size="sm" text="Loading stats..." />
      ) : (
        isHr ? (
          <>
            <DashboardQuickLinks cards={hrQuickLinks} />

            <div className="grid gap-6 xl:grid-cols-[1.5fr_1fr]">
              <HiringFunnel data={funnelData} />
              <PendingActionsCards data={pendingActions} />
            </div>

            <div className="grid gap-6 md:grid-cols-2">
              <AssessmentRoundsTable data={assessmentRounds} />
              <RiskAlertsTable data={visibleRiskAlerts} />
            </div>

            <div className="grid gap-6 xl:grid-cols-[1.6fr_1fr]">
              <MonthlyHiringTrend data={monthlyTrendData} />
              <AssessmentQualityInsights data={assessmentQualityData} />
            </div>

            <RoleSummaryTable id="role-summary" data={roleSummary} />
            <StageStudentTable id="stage-students" data={stageStudents} />
          </>
        ) : (
          <>
            <ATSStatsCards
              totalColleges={atsStats.totalColleges}
              completed={atsStats.completed}
              upcoming={atsStats.upcoming}
              totalStudents={atsStats.totalStudents}
              totalSelected={atsStats.totalSelected}
              totalOffered={atsStats.totalOffered}
              totalAccepted={atsStats.totalAccepted}
              totalJoined={atsStats.totalJoined}
            />

            <div className="grid gap-6 xl:grid-cols-[1.5fr_1fr]">
              <HiringFunnel data={funnelData} />
              <PendingActionsCards data={pendingActions} />
            </div>

            <div className="grid gap-6 md:grid-cols-2">
              <UpcomingCollegesTable id="upcoming-colleges" data={upcomingColleges} />
              <AssessmentRoundsTable data={assessmentRounds} />
            </div>

            <div className="grid gap-6 md:grid-cols-2">
              <UpcomingWorkloadCard
                next7Days={upcomingWorkload.next7Days}
                next14Days={upcomingWorkload.next14Days}
                data={upcomingWorkload.rows}
              />
              <RiskAlertsTable data={visibleRiskAlerts} />
            </div>

            <div className="grid gap-6 xl:grid-cols-[1.6fr_1fr]">
              <MonthlyHiringTrend data={monthlyTrendData} />
              <AssessmentQualityInsights data={assessmentQualityData} />
            </div>

            <RoleTargetTracker data={roleTargetRows} />

            <div className="grid gap-6 md:grid-cols-2">
              <CollegeSummaryTable id="college-summary" data={collegeSummary} />
              <RoleSummaryTable id="role-summary" data={roleSummary} />
            </div>

            <StageStudentTable id="stage-students" data={stageStudents} />

            <div className="space-y-4">
              <div>
                <h2 className="text-2xl font-semibold tracking-tight">Distribution Views</h2>
                <p className="text-sm text-muted-foreground">
                  Compare candidate spread across colleges and roles for both total pipeline and final-stage movement.
                </p>
              </div>

              <div className="grid gap-6 xl:grid-cols-2">
                <SimplePieChart
                  id="pie-college-total"
                  title="College Wise Total Candidates"
                  description="Ranked view of overall pipeline volume by college"
                  data={pieData.collegeTotals}
                />
                <SimplePieChart
                  id="pie-role-total"
                  title="Role Wise Total Candidates"
                  description="Ranked view of overall pipeline volume by role"
                  data={pieData.roleTotals}
                />
                <SimplePieChart
                  id="pie-college-stage"
                  title="College Wise Final Stage Candidates"
                  description="Ranked view of candidates who cleared technical or HR rounds"
                  data={pieData.collegeStages}
                />
                <SimplePieChart
                  id="pie-role-stage"
                  title="Role Wise Final Stage Candidates"
                  description="Ranked view of final-stage movement by role"
                  data={pieData.roleStages}
                />
              </div>
            </div>
          </>
        )
      )}
    </div>
  );
}
